import React, { useState, type ChangeEvent, type FocusEvent } from "react";
import ShipIcon from "images/svg/icon-ship.svg";
import { debounce } from "helpers/utility-helpers";
import { overrideTailwindClasses } from "tailwind-override";
import classnames from "classnames";

type ZipInputProps = {
  classNames?: string;
  error: boolean;
  onZipSubmit: (zip: string) => void;
  setError: (isError: boolean) => void;
  zipCode: string;
};

const ZipInput = ({ zipCode, error = false, setError = () => {}, onZipSubmit, classNames = "" }: ZipInputProps) => {
  const [zipValue, setZipValue] = useState(zipCode);

  const baseClassnames = classnames(
    "flex",
    "items-center",
    "gap-1.5",
    "rounded-md",
    "border",
    "h-12",
    "md:py-2",
    "px-1",
    "bg-white",
    "overflow-hidden",
    {
      ["border-mutedRed"]: error,
      ["border-lighterGray"]: !error,
    }
  );

  const classes = overrideTailwindClasses(`${baseClassnames} ${classNames}`);

  const regexForFiveDigits = /^\d{5}$/;

  const onZipFocus = () => {
    setZipValue("");
  };

  const onZipBlur = (e: FocusEvent<HTMLInputElement>) => {
    const targetZipValue: string = e.target.value;
    if (regexForFiveDigits.test(targetZipValue)) {
      return;
    }
    setZipValue("Zip");
    setError(true);
  };

  const handleZipChange = debounce((value: string) => {
    onZipSubmit(value);
  }, 500);

  const changeZip = (e: ChangeEvent<HTMLInputElement>) => {
    const value: string = e.target.value;
    setZipValue(e.target.value);

    if (!regexForFiveDigits.test(value)) {
      setError(true);
    } else if (regexForFiveDigits.test(value)) {
      handleZipChange(value);
      setError(false);
    }
  };

  return (
    <div className={classes}>
      <ShipIcon className="md:h-5 mx-1" />
      <input
        type="text"
        value={zipValue}
        className={`!border-none !p-0 h-8 !text-md ${error ? "!text-mutedRed" : "text-black"} md:text-lg md:font-serif`}
        onChange={changeZip}
        onFocus={onZipFocus}
        onBlur={onZipBlur}
        maxLength={5}
        data-test-id="zip-code-input"
      />
    </div>
  );
};

export default ZipInput;
